import { useEffect, useState } from "react";
import api from "@/lib/api";

const SOURCE_BADGE = {
  segremat: { c: "#1565c0", bg: "#e3f2fd", l: "Segremat" },
  import:   { c: "#558b2f", bg: "#f1f8e9", l: "Importata" },
  manual:   { c: "#6a1b9a", bg: "#f3e5f5", l: "Manuale" },
  comunicator: { c: "#bf360c", bg: "#fff3e0", l: "Comunicator" },
};

/**
 * Modal per gestire le email di un alunno.
 * - source 'segremat' è read-only (verrà sovrascritto al prossimo sync)
 * - le altre possono essere modificate/eliminate
 * - aggiunta come 'manual' di default
 */
export default function AlunnoEmailsModal({ cod, alunnoLabel = "", onClose, onUpdated, defaultSource = "manual" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const carica = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/alunni-emails/${cod}`);
      setItems(r.data?.items || []);
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, [cod]);

  const aggiungi = async () => {
    if (!newEmail || !newEmail.includes("@")) { setErr("Email non valida"); return; }
    setSaving(true); setErr(null);
    try {
      await api.post(`/alunni-emails/${cod}`, { email: newEmail, note: newNote, source: defaultSource });
      setNewEmail(""); setNewNote("");
      await carica();
      onUpdated?.();
    } catch (e) { setErr(e.response?.data?.detail || "Errore aggiunta"); }
    finally { setSaving(false); }
  };

  const elimina = async (eid) => {
    if (!window.confirm("Eliminare questa email?")) return;
    try {
      await api.delete(`/alunni-emails/${cod}/${eid}`);
      await carica();
      onUpdated?.();
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 750, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div data-testid="alunno-emails-modal"
        style={{ background: "white", width: "100%", maxWidth: 700, borderRadius: 6, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#1565c0", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong>📧 Email — {alunnoLabel || `Cod. ${cod}`}</strong>
          <button onClick={onClose} data-testid="ae-close"
            style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          {err && <div style={{ marginBottom: 10, padding: 8, background: "#fff0f0", color: "#c00", fontSize: 12, border: "1px solid #fcc", borderRadius: 3 }}>{err} <button onClick={() => setErr(null)} style={{ marginLeft: 8, border: "none", background: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button></div>}

          <div style={{ background: "white", border: "1px solid #ddd", marginBottom: 14 }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead style={{ background: "#1f3864", color: "white" }}>
                <tr>
                  <th style={{ padding: 6, textAlign: "left" }}>EMAIL</th>
                  <th style={{ padding: 6, width: 100, textAlign: "center" }}>ORIGINE</th>
                  <th style={{ padding: 6, width: 160 }}>NOTE</th>
                  <th style={{ padding: 6, width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} style={{ padding: 14, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 14, textAlign: "center", color: "#888" }}>Nessuna email registrata per questo alunno.</td></tr>
                )}
                {!loading && items.map(it => {
                  const b = SOURCE_BADGE[it.source] || SOURCE_BADGE.manual;
                  return (
                    <tr key={it.id} style={{ borderTop: "1px solid #eee" }} data-testid={`ae-row-${it.id}`}>
                      <td style={{ padding: 6, fontFamily: "Consolas, monospace", color: "#0d47a1" }}>{it.email}</td>
                      <td style={{ padding: 6, textAlign: "center" }}>
                        <span style={{ padding: "1px 8px", background: b.bg, color: b.c, fontSize: 10, fontWeight: 700, borderRadius: 2 }}>{b.l}</span>
                      </td>
                      <td style={{ padding: 6, fontSize: 11, color: "#666" }}>{it.note || ""}</td>
                      <td style={{ padding: 6, textAlign: "center" }}>
                        <button onClick={() => elimina(it.id)} data-testid={`ae-del-${it.id}`}
                          title={it.source === "segremat" ? "L'email Segremat sarà ri-aggiunta al prossimo sync" : "Elimina"}
                          style={{ background: "#c62828", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ background: "#f5fafa", border: "1px solid #00838f", padding: 12, borderRadius: 3 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#00838f", marginBottom: 6 }}>+ AGGIUNGI NUOVA EMAIL</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                placeholder="nuova@email.it" data-testid="ae-new-email"
                onKeyDown={e => { if (e.key === "Enter") aggiungi(); }}
                style={{ flex: 1, minWidth: 220, padding: 7, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace" }} />
              <input type="text" value={newNote} onChange={e => setNewNote(e.target.value)}
                placeholder="Nota (opzionale)" data-testid="ae-new-note"
                style={{ flex: 0.6, minWidth: 140, padding: 7, border: "1px solid #ccc", fontSize: 12 }} />
              <button onClick={aggiungi} disabled={saving || !newEmail} data-testid="ae-new-save"
                style={{ background: (saving || !newEmail) ? "#bbb" : "#00838f", color: "white", border: "none", padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 3 }}>
                {saving ? "..." : "+ Aggiungi"}
              </button>
            </div>
            <div style={{ marginTop: 6, fontSize: 10, color: "#666" }}>
              Le email aggiunte qui sono di origine <b>Manuale</b> e non vengono modificate dalla sincronizzazione con Segremat.
            </div>
          </div>
        </div>

        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose}
            style={{ background: "#00838f", color: "white", border: "none", padding: "8px 22px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}
